import { MONTHS } from "./constants";

export function formatDates(d) {
  if (!d) return "Not set";
  if (d.flexible) return "Flexible — no fixed dates";
  if (!d.fromMonth) return "Not set";
  return `${d.fromMonth} ${d.fromYear} – ${d.toMonth} ${d.toYear}`;
}
// Initials on the brand red, drawn locally. This used to be ui-avatars.com,
// which meant every member's name was sent to a third party each time a
// profile without a photo was shown.
export function avatarFallback(name) {
  const initials = (name || "?").trim().split(/\s+/).slice(0, 2)
    .map(w => [...w][0]?.toUpperCase() ?? "").join("") || "?";
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400">' +
    '<rect width="400" height="400" fill="#B23A26"/>' +
    '<text x="50%" y="50%" dy=".35em" text-anchor="middle" fill="#fff" ' +
    'font-family="system-ui, sans-serif" font-size="160" font-weight="600">' +
    initials.replace(/[<>&"']/g, "") + "</text></svg>";
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
// Convert {fromMonth, fromYear, toMonth, toYear} to a numeric range for overlap check
export function formatTravelDates(d) {
  if (!d) return "Flexible";
  if (d.flexible) return "Flexible";
  if (!d.fromMonth || !d.fromYear || !d.toMonth || !d.toYear) return "Flexible";
  return `${d.fromMonth} ${d.fromYear} – ${d.toMonth} ${d.toYear}`;
}

// The database stores travel windows as real date columns so overlap can be
// filtered and indexed server-side. The UI still works in month/year pickers,
// so these two convert between the shapes.
export function travelDatesToColumns(d) {
  const flexible = { travel_from: null, travel_to: null, dates_flexible: true };
  if (!d || d.flexible) return flexible;

  const fromIdx = MONTHS.indexOf(d.fromMonth);
  const toIdx = MONTHS.indexOf(d.toMonth);
  if (fromIdx === -1 || toIdx === -1 || !d.fromYear || !d.toYear) return flexible;

  const from = new Date(Date.UTC(Number(d.fromYear), fromIdx, 1));
  const to = new Date(Date.UTC(Number(d.toYear), toIdx + 1, 0)); // last day of month
  return {
    travel_from: from.toISOString().slice(0, 10),
    travel_to: to.toISOString().slice(0, 10),
    dates_flexible: false,
  };
}

export function columnsToTravelDates(row) {
  const flexible = { flexible: true, fromMonth: "", fromYear: "", toMonth: "", toYear: "" };
  if (!row || row.dates_flexible || !row.travel_from || !row.travel_to) return flexible;

  const from = new Date(row.travel_from);
  const to = new Date(row.travel_to);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return flexible;

  return {
    flexible: false,
    fromMonth: MONTHS[from.getUTCMonth()],
    fromYear: String(from.getUTCFullYear()),
    toMonth: MONTHS[to.getUTCMonth()],
    toYear: String(to.getUTCFullYear()),
  };
}

// Age is derived from date of birth so it can never drift or be overstated.
export function ageFromDob(dob) {
  if (!dob) return null;
  const born = new Date(dob);
  if (Number.isNaN(born.getTime())) return null;

  const today = new Date();
  let age = today.getUTCFullYear() - born.getUTCFullYear();
  const monthDiff = today.getUTCMonth() - born.getUTCMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getUTCDate() < born.getUTCDate())) age--;
  return age;
}

// get_deck and get_matches_overview return the same profile shape; this maps
// one row to the object the card and profile components expect.
export function rowToUser(row) {
  const photo = row.avatar_url || avatarFallback(row.name);
  return {
    id: row.id ?? row.other_id,
    name: row.name,
    age: row.age,
    photo,
    photos: [photo, ...(row.extra_photos || []).filter(Boolean)],
    from: row.from_location,
    bio: row.bio,
    regions: row.regions || [],
    activities: row.activities || [],
    budget: row.budget,
    duration: row.duration,
    vibe: row.vibe,
    travelSpeed: row.travel_speed,
    accommodation: row.accommodation,
    travelDates: columnsToTravelDates(row),
    verified: row.is_verified,
    ageVerified: row.age_verified,
    tripsCount: row.trips_count || 0,
    gender: row.gender,
    languages: row.languages || [],
  };
}
